import React, { useState, useEffect, useCallback, useRef } from 'react';
import QRCode from 'qrcode';
import socket from '../services/socket';
import { API_BASE_URL } from '../config';
import '../styles/RegistrationCodesDisplay.css';

const CYCLE_INTERVAL = 3000; // Rotate codes every 3 seconds

const RegistrationCodesDisplay = () => {
  const [availableCodes, setAvailableCodes] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ used: 0, total: 0 });
  const [recentlyUsed, setRecentlyUsed] = useState(null);
  const codesRef = useRef([]);

  // Keep ref in sync for use in interval callback
  useEffect(() => {
    codesRef.current = availableCodes;
  }, [availableCodes]);

  // Get the base URL for QR codes
  const getRegistrationUrl = (code) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/register?code=${code}`;
  };

  // Fetch available codes from server
  const fetchCodes = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/registration-codes/available`);
      const data = await response.json();

      if (data.success) {
        setAvailableCodes(data.codes || []);
        setStats({ used: data.usedCount || 0, total: data.totalCount || 0 });

        if (!data.codes || data.codes.length === 0) {
          setError('No registration codes available');
        } else {
          setError(null);
        }
      } else {
        setError(data.message || 'Failed to fetch codes');
      }
    } catch (err) {
      console.error('Error fetching codes:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-cycle through codes every 3 seconds
  useEffect(() => {
    if (availableCodes.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % codesRef.current.length);
    }, CYCLE_INTERVAL);

    return () => clearInterval(interval);
  }, [availableCodes.length]);

  const currentCode = availableCodes[currentIndex % availableCodes.length] || null;

  // Generate QR code image
  useEffect(() => {
    if (currentCode) {
      const url = getRegistrationUrl(currentCode);
      QRCode.toDataURL(url, {
        width: 600,
        margin: 2,
        color: {
          dark: '#00ff41',
          light: '#000000'
        },
        errorCorrectionLevel: 'M'
      })
        .then(dataUrl => setQrDataUrl(dataUrl))
        .catch(err => console.error('QR generation error:', err));
    } else {
      setQrDataUrl(null);
    }
  }, [currentCode]);

  // Initial fetch + periodic refresh (pick up newly used codes)
  useEffect(() => {
    fetchCodes();
    const interval = setInterval(fetchCodes, 30000);
    return () => clearInterval(interval);
  }, [fetchCodes]);

  // Listen for registration events
  useEffect(() => {
    const handleCodeUsed = (data) => {
      console.log('Code used:', data);

      if (data.code) {
        setRecentlyUsed(data.code);
        setTimeout(() => setRecentlyUsed(null), 3000);
      }

      // Remove the used code from the pool
      setAvailableCodes(prev => {
        const updated = prev.filter(c => c !== data.code);
        if (updated.length === 0) {
          setError('All codes have been used');
        }
        return updated;
      });

      setStats(prev => ({
        ...prev,
        used: prev.used + 1
      }));
    };

    const handlePlayerRegistered = () => {
      // Refresh codes to ensure sync
      fetchCodes();
    };

    socket.on('registration-code-used', handleCodeUsed);
    socket.on('player-registered', handlePlayerRegistered);

    return () => {
      socket.off('registration-code-used', handleCodeUsed);
      socket.off('player-registered', handlePlayerRegistered);
    };
  }, [fetchCodes]);

  if (loading) {
    return (
      <div className="reg-codes-display">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>INITIALIZING REGISTRATION SYSTEM...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reg-codes-display">
      <div className="scanlines"></div>

      <div className="reg-header">
        <div className="company-logo">KOMOK</div>
        <div className="header-subtitle">EMPLOYEE ONBOARDING TERMINAL</div>
      </div>

      <div className="reg-main">
        {error ? (
          <div className="error-state">
            <div className="error-icon">!</div>
            <p className="error-message">{error}</p>
            <p className="error-sub">Please contact personnel department</p>
          </div>
        ) : currentCode ? (
          <>
            {recentlyUsed && (
              <div className="code-used-notification">
                CODE {recentlyUsed} CLAIMED
              </div>
            )}

            <div className="qr-container">
              <div className="qr-frame">
                {qrDataUrl && (
                  <img src={qrDataUrl} alt="Registration QR Code" className="qr-image" />
                )}
              </div>
              <div className="qr-glow"></div>
            </div>

            <div className="code-display">
              <span className="code-label">ACCESS CODE:</span>
              <span className="code-value">{currentCode}</span>
            </div>

            <div className="instructions">
              <p className="instruction-main">SCAN TO BEGIN REGISTRATION</p>
              <p className="instruction-sub">Present mobile device to scanner</p>
            </div>
          </>
        ) : (
          <div className="exhausted-state">
            <div className="exhausted-icon">X</div>
            <p>REGISTRATION CAPACITY REACHED</p>
            <p className="exhausted-sub">All access codes have been claimed</p>
          </div>
        )}
      </div>

      <div className="reg-footer">
        <div className="stat">
          <span className="stat-label">REGISTERED</span>
          <span className="stat-value">{stats.used}</span>
        </div>
        <div className="stat">
          <span className="stat-label">REMAINING</span>
          <span className="stat-value">{stats.total - stats.used}</span>
        </div>
        <div className="stat">
          <span className="stat-label">CAPACITY</span>
          <span className="stat-value">{stats.total}</span>
        </div>
      </div>
    </div>
  );
};

export default RegistrationCodesDisplay;
